"use client";

import Link from "next/link";
import { UserButton, useUser } from "@clerk/nextjs";

const linkClass =
  "text-foreground/80 transition-colors duration-150 hover:text-foreground";

/**
 * Signed-in/out swap for the public header (see ../layout.tsx). A client
 * leaf component on purpose: `Show`, imported from the top-level
 * `@clerk/nextjs` package in this installed version (7.6.0), is actually
 * the APP-ROUTER SERVER COMPONENT variant — `dist/types/index.d.ts`
 * re-types it via `ServerComponentsServerModuleTypes`, and its runtime
 * (`dist/esm/app-router/server/controlComponents.js`) is `async function
 * Show(props) { const { userId } = await auth(...); ... }`. Using that
 * `Show` in the header would call `auth()` on every request and force the
 * entire `(public)` route group ("/" and "/directory", the SEO surface)
 * out of static rendering. There IS a client-only `Show`/`SignedIn`
 * equivalent, but it lives in `@clerk/react`, which is a transitive,
 * undeclared dependency here (only `@clerk/nextjs` is in package.json;
 * `node_modules/@clerk/` has no top-level `react` entry) — not safe to
 * import per CONTRACTS.md's "no new dependencies" rule, and it wouldn't
 * resolve from our source files under pnpm's isolated layout anyway.
 *
 * `useUser()` IS a genuine client-side hook re-exported from
 * `@clerk/nextjs` (`client-boundary/hooks`), so this component resolves
 * signed-in state entirely in the browser after hydration — no server
 * `auth()` call, parent layout stays a plain static Server Component.
 * Renders nothing until Clerk reports loaded, to avoid a signed-in/out
 * flash (same behavior the old `<SignedIn>/<SignedOut>` had).
 */
export function PublicAuthNav() {
  const { isLoaded, isSignedIn } = useUser();

  // Render the signed-out link while Clerk is still loading, and during the
  // SSR pass where `isLoaded` is always false. Returning null there left the
  // public header with nothing but "Directory" — no way in for a visitor,
  // and nothing for crawlers, which never execute Clerk. A brief "Sign in"
  // that swaps to the signed-in controls after hydration is the safer
  // failure mode than an empty nav.
  if (!isLoaded || !isSignedIn) {
    return (
      <Link href="/sign-in" className={linkClass}>
        Sign in
      </Link>
    );
  }

  return (
    <>
      <Link href="/portal" className={linkClass}>
        My portal
      </Link>
      <UserButton afterSwitchSessionUrl="/" />
    </>
  );
}
